import { motion } from "framer-motion";
import { BUY_URL, X_URL } from "../constants";
import { XIcon } from "./XIcon";
import { CopyCA } from "./CopyCA";
import { StatsBar } from "./StatsBar";
import { StormMeter } from "./StormMeter";
import { SmartImage } from "./SmartImage";

export const Hero = ({ stormVolume, stormLevel, stats }) => (
  <header id="top" className="relative pt-28 sm:pt-36 pb-10 px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto">
    <div className="grid lg:grid-cols-[1.15fr_1fr] gap-12 items-center">
      <div className="text-left">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="font-bebas tracking-[0.35em] text-[#14F195] text-lg mb-4 flicker"
          data-testid="hero-kicker"
        >
          HE CALLED THE BOTTOM. TWITTER CALLED HIM CRAZY.
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="font-marker text-6xl sm:text-7xl lg:text-8xl leading-none"
          data-testid="hero-title"
        >
          <span className="gradient-sol text-glow-green">$CURB</span>
        </motion.h1>
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.22 }}
          className="font-marker text-2xl sm:text-3xl lg:text-4xl text-[#c9a8ff] text-glow-purple mt-3 -rotate-1"
          data-testid="hero-subtitle"
        >
          THE SOLANA BULL
        </motion.h2>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.35 }}
          className="text-zinc-300 text-base sm:text-lg mt-6 max-w-xl leading-relaxed"
          data-testid="hero-tagline"
        >
          December 2022. SOL at <span className="text-[#14F195] font-semibold">$8</span>. Everyone
          wrote the obituary. One man read the chart differently — and posted it out loud.
          Eighteen months later, SOL was north of{" "}
          <span className="text-[#14F195] font-semibold">$200</span>.{" "}
          <span className="font-marker text-[#14F195]">That's Curb.</span>
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="mt-8 flex flex-wrap items-center gap-4"
        >
          <a
            href={BUY_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-buy-button"
            className="btn-buy font-bebas tracking-widest text-2xl rounded-full px-8 py-3"
          >
            BUY $CURB
          </a>
          <a
            href={X_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-x-button"
            className="btn-ghost-x rounded-full px-6 py-3 flex items-center gap-2 font-bebas tracking-widest text-xl"
          >
            <XIcon size={18} /> FOLLOW THE BULL
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.65 }}
          className="mt-6"
        >
          <CopyCA testId="hero-copy-ca" />
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0, scale: 0.85, rotate: 3 }}
        animate={{ opacity: 1, scale: 1, rotate: 0 }}
        transition={{ duration: 0.8, delay: 0.25, type: "spring" }}
        className="relative flex justify-center"
      >
        <div className="hero-bull-wrap floaty" style={{ "--float-dur": "7s" }} data-testid="hero-bull-image">
          <SmartImage
            src="/assets/logo.png"
            alt="Curb — The Solana Bull"
            eager
            className="bull-glow w-72 sm:w-96 lg:w-[430px] rounded-3xl border border-[#9945FF]/40"
          />
          <span className="steam absolute bottom-16 left-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-white/30 blur-md" />
          <span className="steam absolute bottom-16 left-[42%] w-6 h-6 rounded-full bg-white/20 blur-md" style={{ animationDelay: "1.2s" }} />
        </div>
        <SmartImage
          src="/assets/penguin.png"
          alt=""
          aria-hidden="true"
          className="floaty absolute -bottom-8 -left-4 sm:-left-10 w-24 sm:w-32 -rotate-6 drop-shadow-[0_0_18px_rgba(153,69,255,0.4)]"
          style={{ "--float-dur": "5s", "--tilt": "-6deg" }}
        />
        <SmartImage
          src="/assets/pepe.png"
          alt=""
          aria-hidden="true"
          className="floaty absolute -top-6 -right-2 sm:-right-8 w-16 sm:w-20 rotate-6"
          style={{ "--float-dur": "4.4s", "--tilt": "6deg" }}
        />
        <SmartImage
          src="/assets/purple-guy.png"
          alt=""
          aria-hidden="true"
          className="floaty absolute -top-8 -left-2 sm:-left-8 w-20 sm:w-24 -rotate-6 drop-shadow-[0_0_18px_rgba(153,69,255,0.4)]"
          style={{ "--float-dur": "5.6s", "--tilt": "-6deg" }}
        />
        <SmartImage
          src="/assets/pixel-guy.png"
          alt=""
          aria-hidden="true"
          className="floaty absolute -bottom-8 -right-2 sm:-right-8 w-20 sm:w-24 rotate-6 drop-shadow-[0_0_18px_rgba(20,241,149,0.35)]"
          style={{ "--float-dur": "4.8s", "--tilt": "6deg" }}
        />
      </motion.div>
    </div>

    <StatsBar stats={stats} />
    <StormMeter volume={stormVolume} level={stormLevel} />
  </header>
);
